import React, {useCallback, useEffect, useRef, useState} from 'react';
import DashboardLayout from "../../layouts/Dashboard";
import {useDispatch, useSelector} from "react-redux";
import CardGeneralRow from "../../components/General/CardGeneralRow";
import {getServiceCategory} from "../../redux/actions/serviceCategory/serviceCategoryActions";
import {getServicesSchedule} from "../../redux/actions/service/serviceActions";
import BreadcrumbsShared from "../../components/Shared/BreadcrumbsShared";
import {GENERAL_ROUTE} from "../../routes/const";
import ClientLayout from "../../layouts/ClientLayout";
import {serviceCRUD} from "../../http/CRUD";
import {
    TextField,
    Paper,
    InputAdornment,
    Typography,
    Box,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    MenuItem,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import {addProductToBasket} from "../../redux/actions/basket/basketActions";
import {showSuccessSnackbar} from "../../redux/actions/userActions";

function General(props) {
    const userLogin = useSelector((state) => state.user);
    const dispatch = useDispatch()
    const {isClient, roles, info} = userLogin;
    const {serviceCategory} = useSelector((state) => state.serviceCategory);

    const isEmployee = info && info.id;
    const hasClientRole = !isEmployee && roles && Array.isArray(roles) && roles.includes('Client');

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const debounceTimer = useRef(null);
    // Локальное состояние для количества и графика каждой найденной услуги
    const [rowState, setRowState] = useState({});

    const {servicesSchedule} = useSelector((state) => state.service);

    useEffect(() => {
        dispatch(getServiceCategory(1, 100))
        dispatch(getServicesSchedule(1, 100))
    }, [dispatch])

    const handleSearch = useCallback(async (query) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        try {
            const trimmed = query.trim();
            const lower = trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
            const upper = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);

            const searchByName = (name) => serviceCRUD.search({
                paging: {skip: 0, take: 20},
                filter: {
                    name: {
                        operand1: name,
                        operator: "like"
                    }
                }
            });

            const queries = [searchByName(lower)];
            if (lower !== upper) {
                queries.push(searchByName(upper));
            }

            const results = await Promise.all(queries);
            const merged = new Map();
            results.forEach(res => {
                if (res?.result?.length) {
                    res.result.forEach(svc => merged.set(svc.id, svc));
                }
            });
            const allResults = Array.from(merged.values());
            setSearchResults(allResults);

            // Инициализируем состояние строк (кол-во=1, первый график)
            const initState = {};
            allResults.forEach(svc => {
                if (!rowState[svc.id]) {
                    initState[svc.id] = {
                        qty: 1,
                        scheduleId: servicesSchedule?.[0]?.id || '',
                        scheduleName: servicesSchedule?.[0]?.name || '',
                    };
                }
            });
            setRowState(prev => ({...prev, ...initState}));
        } catch (e) {
            console.error("Ошибка поиска услуг:", e);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    }, [servicesSchedule, rowState]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            handleSearch(value);
        }, 400);
    };

    const handleQtyChange = (svcId, value) => {
        const qty = Math.max(1, parseInt(value) || 1);
        setRowState(prev => ({...prev, [svcId]: {...prev[svcId], qty}}));
    };

    const handleScheduleChange = (svcId, value) => {
        const schedule = servicesSchedule?.find(s => s.id === value);
        setRowState(prev => ({
            ...prev,
            [svcId]: {...prev[svcId], scheduleId: value, scheduleName: schedule?.name || ''}
        }));
    };

    const handleAddToBasket = (svc) => {
        const state = rowState[svc.id] || {qty: 1, scheduleId: servicesSchedule?.[0]?.id, scheduleName: servicesSchedule?.[0]?.name};
        const row = {
            id: svc.id,
            name: svc.name,
            qty: state.qty,
            price: svc.price,
            summary: svc.price * state.qty,
            schedule: state.scheduleName,
            scheduleId: state.scheduleId,
        };
        dispatch(addProductToBasket(row));
        dispatch(showSuccessSnackbar("Добавлено в корзину!", "success"));
    };

    const SearchBlock = (
        <Box sx={{mb: 3, mt: 1}}>
            <TextField
                sx={{maxWidth: 600}}
                fullWidth
                variant="outlined"
                placeholder="Поиск по всем услугам..."
                value={searchQuery}
                onChange={handleInputChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action"/>
                        </InputAdornment>
                    ),
                    endAdornment: searching ? (
                        <InputAdornment position="end">
                            <CircularProgress size={20}/>
                        </InputAdornment>
                    ) : null
                }}
                size="small"
            />
            {searchResults.length > 0 && (
                <Paper elevation={2} sx={{mt: 2, overflowX: 'auto'}}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">№</TableCell>
                                <TableCell align="center">Наименование услуги</TableCell>
                                <TableCell align="center">Цена</TableCell>
                                <TableCell align="center">Количество</TableCell>
                                <TableCell align="center">График</TableCell>
                                <TableCell align="center">Сумма</TableCell>
                                <TableCell align="center">Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {searchResults.map((svc) => {
                                const st = rowState[svc.id] || {qty: 1, scheduleId: ''};
                                const summary = (svc.price || 0) * (st.qty || 1);
                                return (
                                    <TableRow key={svc.id}>
                                        <TableCell align="center">{svc.id}</TableCell>
                                        <TableCell align="center">{svc.name}</TableCell>
                                        <TableCell align="center">{svc.price} ₸</TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                size="small"
                                                label="Кол. *"
                                                value={st.qty}
                                                onChange={(e) => handleQtyChange(svc.id, e.target.value)}
                                                inputProps={{min: 1, style: {textAlign: 'center', width: 50}}}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                select
                                                size="small"
                                                label="График *"
                                                value={st.scheduleId}
                                                onChange={(e) => handleScheduleChange(svc.id, e.target.value)}
                                                sx={{minWidth: 160}}
                                            >
                                                {servicesSchedule?.map(s => (
                                                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                                ))}
                                            </TextField>
                                        </TableCell>
                                        <TableCell align="center">{summary} ₸</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleAddToBasket(svc)}
                                                title="Добавить в корзину"
                                            >
                                                <AddShoppingCartIcon/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>
            )}
            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                    Услуги не найдены
                </Typography>
            )}
        </Box>
    );

    return (
        <>
            {!hasClientRole && <DashboardLayout>
                <BreadcrumbsShared breadcrumbs={[
                    {text: 'Главная', link: GENERAL_ROUTE}
                ]}/>
                {SearchBlock}
                <CardGeneralRow serviceCategory={serviceCategory}/>
            </DashboardLayout>}
            {hasClientRole && <ClientLayout>
                <BreadcrumbsShared breadcrumbs={[
                    {text: 'Главная', link: GENERAL_ROUTE}
                ]}/>
                {SearchBlock}
                <CardGeneralRow serviceCategory={serviceCategory}/>
            </ClientLayout>}
        </>
    );
}

export default General;